import React from 'react';
import { useForm } from 'react-hook-form';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { AppointmentDetails } from '../../types';

export const AppointmentDetailsForm: React.FC = () => {
  const { currentEnquiry, updateAppointmentDetails } = useEnquiryStore();
  const { register, handleSubmit, formState: { errors } } = useForm<AppointmentDetails>({
    defaultValues: currentEnquiry.appointmentDetails
  });

  const onSubmit = (data: AppointmentDetails) => {
    updateAppointmentDetails(data);
  };

  const handleInputChange = (field: keyof AppointmentDetails, value: string | boolean) => {
    updateAppointmentDetails({ [field]: value });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Appointment Details</h2>
        <p className="text-sm text-gray-600">Schedule the appointment and specify requirements.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              Appointment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="date"
              {...register('date', { required: 'Appointment date is required' })}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>}
          </div>

          <div>
            <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
              Appointment Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              id="time"
              {...register('time', { required: 'Appointment time is required' })}
              onChange={(e) => handleInputChange('time', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.time && <p className="mt-1 text-sm text-red-600">{errors.time.message}</p>}
          </div>
        </div>

        <div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="samplesToTake"
              {...register('samplesToTake')}
              onChange={(e) => handleInputChange('samplesToTake', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="samplesToTake" className="ml-2 block text-sm text-gray-900">
              Samples to take
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-600">Check if you need to bring product samples to the appointment.</p>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Appointment Description
          </label>
          <textarea
            id="description"
            {...register('description')}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Additional details about the appointment, special requirements, access instructions, etc."
          />
        </div>

        <div className="bg-blue-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Appointment Tips</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Confirm the appointment 24 hours in advance</li>
                  <li>Bring measuring tools and product samples if selected</li>
                  <li>Note any access restrictions or special requirements</li>
                  <li>Allow extra time for detailed measurements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};